/**
 * The next tastings that can still be booked, for "The shop in Frome" (sections/shop-visit.liquid)
 * and for "More tastings coming up" at the foot of every ticket page (sections/more-tastings.liquid).
 *
 * WHY IT IS FETCHED, NOT RENDERED (GV-09, 2026-10-01). The section used to list two hand-picked
 * event products, and they went stale: it showed 2024 tastings while the real calendar ran on. The
 * dates do not exist anywhere Liquid can read. The tickets are Experiences app products, and their
 * Shopify data carries no date, no seat count and no tracked inventory (every variant reads
 * available, with negative quantities): read over the Admin API on 2026-10-01. The app keeps the
 * sessions itself and serves them to its own calendar on /pages/events from a public endpoint,
 * `productsAvailability`, which takes a date window and returns each experience with its timeslots,
 * `startsAt`, `unitsLeft` and its booking cutoff. This reads the same endpoint the same way.
 *
 * WHAT COUNTS AS "NEXT". A timeslot that starts after now plus the experience's booking cutoff (set
 * per tasting in the app: none, 2h, 24h or 48h on this shop, read 2026-10-01), on an active, unarchived experience, with at
 * least one place left. Sorted by start, first `limit` (2) shown. Fewer than that shows fewer;
 * none, or a failed or slow request, leaves the section's "See all tastings" link on its own, which
 * is also what a visitor without JavaScript sees. Nothing is ever typed in by hand.
 *
 * COST. One cross-origin POST, no cookies (`credentials: 'omit'`), and only once the section is
 * within 600px of the viewport, so it never competes with the hero.
 *
 * ON A TICKET PAGE (2026-10-02) the list leaves out the tasting being viewed (`data-exclude`, its
 * handle) and takes its class names from the host section (`data-prefix`), so each section styles
 * its own list. It replaced Shopify's "You may also like", which on some tickets recommended gin and
 * wine, and could recommend a past tasting because ticket stock is untracked.
 *
 * `nextTastings` is exported so scripts/tests/tastings-next.test.mjs can hold the selection rule
 * without a browser.
 *
 * THE SAME FEED BLOCKS PRIVATE TASTING DATES (2026-10-29). Jamie hosts a private tasting Monday to
 * Saturday "as long as there isn't an event on in the shop". The booking check on a tasting's page
 * (blocks/tasting-booking.liquid) imports this file and asks `eventDays` for every day with an
 * event, so a day is refused the moment it is in the app's calendar, with nothing typed in twice.
 * Unlike "next", a full or soon-starting event still counts: the shop is busy either way.
 */

const ENDPOINT = 'https://prod-v2-api.experiencesapp.services/rest/productsAvailability';
const WINDOW_DAYS = 120;
const TIMEOUT_MS = 8000;
/* Every event happens in the shop in Frome, whatever zone its timeslot was saved in: on
   2026-10-29 one was saved as Asia/Karachi. */
const SHOP_ZONE = 'Europe/London';

/**
 * The feed for `days` days from `from`. Rejects on a network error, a slow answer or a bad status,
 * so each caller decides what a missing feed means.
 * @param {string} shop the shop's permanent domain
 * @param {number} from epoch ms
 * @param {number} days
 * @returns {Promise<any[]>}
 */
export async function fetchExperiences(shop, from, days) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(`${ENDPOINT}?shop=${encodeURIComponent(shop || '')}`, {
      method: 'POST',
      credentials: 'omit',
      signal: controller.signal,
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({
        startsAt: new Date(from).toISOString(),
        endsAt: new Date(from + days * 864e5).toISOString(),
        tags: '',
      }),
    });
    if (!response.ok) throw new Error(`status ${response.status}`);
    return await response.json();
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Every day the shop has an event on: each timeslot of each active, unarchived experience, full or
 * not, keyed by its date in Frome. Where two events share a day, the earlier one names it.
 * @param {any[]} experiences the endpoint's response
 * @returns {Map<string, string>} YYYY-MM-DD to the event's name
 */
export function eventDays(experiences) {
  const days = new Map();
  if (!Array.isArray(experiences)) return days;
  const format = new Intl.DateTimeFormat('en-GB', { timeZone: SHOP_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' });
  const slots = [];
  for (const exp of experiences) {
    if (!exp || exp.status !== 'active' || exp.archivedAt) continue;
    for (const group of exp.availabilityProducts ?? []) {
      for (const slot of group?.availableTimeslots ?? []) {
        const start = Date.parse(slot?.startsAt);
        if (Number.isFinite(start)) slots.push({ start, name: String(exp.name || exp.summary || '') });
      }
    }
  }
  slots.sort((a, b) => a.start - b.start);
  for (const { start, name } of slots) {
    const part = Object.fromEntries(format.formatToParts(start).map(({ type, value }) => [type, value]));
    const day = `${part.year}-${part.month}-${part.day}`;
    if (!days.has(day)) days.set(day, name);
  }
  return days;
}

/**
 * @param {any[]} experiences the endpoint's response
 * @param {number} now epoch ms
 * @param {number} limit
 * @param {string} [exclude] a handle to leave out: the tasting whose page this is
 * @returns {{ name: string, handle: string, startsAt: string, timezone: string, unitsLeft: number, price: number | null }[]}
 */
export function nextTastings(experiences, now, limit = 2, exclude = '') {
  if (!Array.isArray(experiences)) return [];
  const slots = [];
  for (const exp of experiences) {
    if (!exp || exp.status !== 'active' || exp.archivedAt || !exp.handle) continue;
    if (exclude && exp.handle === exclude) continue;
    const cutoff = Number(exp.bookingCutoffMs) || 0;
    const price = Array.isArray(exp.variants) && typeof exp.variants[0]?.price === 'number' ? exp.variants[0].price : null;
    for (const group of exp.availabilityProducts ?? []) {
      for (const slot of group?.availableTimeslots ?? []) {
        const start = Date.parse(slot?.startsAt);
        if (!Number.isFinite(start) || start - cutoff <= now) continue;
        if (!(Number(slot.unitsLeft) > 0)) continue;
        slots.push({
          name: String(exp.name || exp.summary || ''),
          handle: String(exp.handle),
          startsAt: slot.startsAt,
          timezone: slot.timezone || 'Europe/London',
          unitsLeft: Number(slot.unitsLeft),
          price,
        });
      }
    }
  }
  slots.sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  return slots.slice(0, limit);
}

if (typeof customElements !== 'undefined' && !customElements.get('givino-tastings')) {
  class GivinoTastings extends HTMLElement {
    connectedCallback() {
      if (this.dataset.state) return;
      this.dataset.state = 'waiting';
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer?.disconnect();
          this.load();
        },
        { rootMargin: '600px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async load() {
      this.dataset.state = 'loading';
      const now = Date.now();
      let experiences;
      try {
        experiences = await fetchExperiences(this.dataset.shop || '', now, WINDOW_DAYS);
      } catch {
        this.dataset.state = 'failed';
        return;
      }
      const tastings = nextTastings(experiences, now, Number(this.dataset.limit) || 2, this.dataset.exclude || '');
      this.render(tastings);
      this.dataset.state = tastings.length ? 'ready' : 'empty';
    }

    /** @param {ReturnType<typeof nextTastings>} tastings */
    render(tastings) {
      const list = this.querySelector('ul');
      if (!list || !tastings.length) return;
      const lang = this.dataset.lang || document.documentElement.lang || 'en-GB';
      const root = window.Shopify?.routes?.root || '/';
      const prefix = this.dataset.prefix || 'shop-visit__event';
      list.replaceChildren(
        ...tastings.map((tasting) => {
          const href = `${root}products/${encodeURIComponent(tasting.handle)}`;
          const item = document.createElement('li');
          item.className = prefix;

          const name = document.createElement('a');
          name.className = `${prefix}-name`;
          name.href = href;
          name.textContent = tasting.name;

          const meta = document.createElement('span');
          meta.className = `${prefix}-price`;
          const time = document.createElement('time');
          time.dateTime = tasting.startsAt;
          time.textContent = new Date(tasting.startsAt).toLocaleString(lang, {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: 'numeric',
            minute: '2-digit',
            timeZone: tasting.timezone,
          });
          const parts = [time];
          if (tasting.price != null && this.dataset.currency) {
            parts.push(tasting.price.toLocaleString(lang, { style: 'currency', currency: this.dataset.currency }));
          }
          const left = tasting.unitsLeft === 1 ? this.dataset.leftOne : this.dataset.leftMany;
          if (left) parts.push(left.replace('[count]', String(tasting.unitsLeft)));
          parts.forEach((part, index) => {
            if (index) meta.append(' · ');
            meta.append(part);
          });

          const book = document.createElement('a');
          book.className = `${prefix}-book`;
          book.href = href;
          book.textContent = this.dataset.book || '';
          const hidden = document.createElement('span');
          hidden.className = 'visually-hidden';
          hidden.textContent = `: ${tasting.name}`;
          book.append(hidden);

          item.append(name, meta, book);
          return item;
        })
      );
      list.hidden = false;
    }
  }

  customElements.define('givino-tastings', GivinoTastings);
}
